/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React from 'react';
import {
  Image,
  Card,
  Container,
  Navbar,
  Row,
  Col,
  ButtonGroup,
  OverlayTrigger,
  Tooltip,
  Button,
} from 'react-bootstrap';
import { HashLink } from 'react-router-hash-link';
import moment from 'moment-timezone';
import { toHeaderCase } from 'js-convert-case';
import { SOCIAL_MEDIA_ICON } from './socialMedia.icon';
export interface IFooterLinkProps {
  name: string;
  link: string;
  target?: '_blank' | '_self'  | any;
}

export interface IFooterProps {
  footerBrandLink?: string;
  footerBrandLogo: string;
  footerBrandTitle: string;
  footerBrandTitleStyle?: any;
  footerBrandDescription?: string;
  footerUsefulLinkLabel?: string;
  footerUsefulLinks?: IFooterLinkProps[];
  footerServiceLinkLabel?: string;
  footerServiceLinks?: IFooterLinkProps[];
  footerContactLabel?: string;
  footerContactLinks?: {
    name: string;
    value: string;
  }[];
  footerCopyright: {
    brandTitle: string;
    brandLogo: string;
    brandLogoHeight?: number;
    brandLink: string;
    copyrightTitle: string;
    startYear: string;
    target?: '_blank' | '_self';
  };
  socialLinks?: {
    name:
      | 'facebook'
      | 'twitter'
      | 'instagram'
      | 'linkedin'
      | 'youtube'
      | 'website';
    link: string;
  }[];
}

export const Footer = ({
  footerBrandLogo,
  footerBrandDescription,
  footerBrandLink,
  footerBrandTitle,
  footerBrandTitleStyle,
  footerUsefulLinkLabel,
  footerUsefulLinks,
  footerServiceLinks,
  footerServiceLinkLabel,
  footerContactLabel,
  footerContactLinks,
  footerCopyright,
  socialLinks,
}: IFooterProps) => {
  const currentYear = moment().get('year');

  const getFirstLetterOfEachWord = (text: string) =>
    text?.match(/\b\w/g)?.join('');

  const isValidUrl = (urlString: string) => {
    try {
      return Boolean(new URL(urlString));
    } catch (e) {
      return false;
    }
  };

  const _getIcon = (Icon: any) => {
    let _icon: any = [];
    if (typeof Icon === 'string') {
      if (isValidUrl(Icon)) {
        _icon.push(
          <Image
            key={`image-icon`}
            src={Icon}
            width={20}
            height={20}
            style={{ objectFit: 'contain' }}
          />
        );
      } else {
        const HeroIcon = require('@heroicons/react/outline');
        const NavIcon = HeroIcon[Icon];
        if (NavIcon) {
          _icon.push(
            <NavIcon
              key={`hero-icon`}
              src={Icon}
              className="icon icon-xs text-white"
            />
          );
        }
      }
      return _icon;
    }
    if (Icon) {
      _icon.push(
        <Icon key={`hero-icon-2`} className="icon icon-xs text-white" />
      );
    }
    return _icon;
  };

  const _renderSocialMediaIconIf = () => {
    const _comp: any = [];
    if (socialLinks) {
      for (const [index, socialLink] of socialLinks?.entries()) {
        _comp.push(
          <ButtonGroup
            key={`profile-card-smi-${index}`}
            className="mb-3 me-3 mb-md-0  d-md-inline-flex"
          >
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip className="m-0">
                  {toHeaderCase(socialLink?.name ?? '')}
                </Tooltip>
              }
            >
              <Button
                variant="transparent"
                className={'bg-white p-1'}
                onClick={() => window.open(socialLink.link, '__blank')}
              >
                {_getIcon(SOCIAL_MEDIA_ICON[socialLink.name]?.icon)}
              </Button>
            </OverlayTrigger>
          </ButtonGroup>
        );
      }
    }
    return _comp;
  };

  const _renderBrand = () => {
    return (
      <Col md={5}>
        <Navbar.Brand
          as={HashLink}
          to={footerBrandLink}
          className="me-lg-3 mb-3 d-flex align-items-center"
        >
          <Image src={footerBrandLogo} />
          <span className="ms-2 brand-text" style={footerBrandTitleStyle}>
            {footerBrandTitle}
          </span>
        </Navbar.Brand>
        {footerBrandDescription && <p>{footerBrandDescription}</p>}
        <div className="d-block">{_renderSocialMediaIconIf()}</div>
      </Col>
    );
  };

  const _renderUsefulLinks = () => {
    return (
      <Col xs={6} md={2} className="mb-5 mb-lg-0">
        {footerUsefulLinkLabel && (
          <span className="h5">{footerUsefulLinkLabel}</span>
        )}
        {footerUsefulLinks && (
          <ul className="links-vertical mt-2">
            {footerUsefulLinks?.map((item, index) => (
              <li key={`footer-list-${index}`}>
                <Card.Link
                  target={item?.target ? '_blank' : '_self'}
                  href={item?.link}
                >
                  {item?.name}
                </Card.Link>
              </li>
            ))}
          </ul>
        )}
      </Col>
    );
  };

  const _renderServiceLinks = () => {
    return (
      <Col xs={6} md={2} className="mb-5 mb-lg-0">
        {footerServiceLinkLabel && (
          <span className="h5">{footerServiceLinkLabel}</span>
        )}
        {footerServiceLinks && (
          <ul className="links-vertical mt-2">
            {footerServiceLinks?.map((item, index) => (
              <li key={`footer-list-${index}`}>
                <Card.Link
                  target={item?.target ? '_blank' : '_self'}
                  href={item?.link}
                >
                  {item?.name}
                </Card.Link>
              </li>
            ))}
          </ul>
        )}
      </Col>
    );
  };

  const _renderAddress = () => {
    return (
      <Col xs={6} md={3} className="mb-5 mb-lg-0">
        {footerContactLabel && <span className="h5">{footerContactLabel}</span>}
        {footerContactLinks && (
          <ul className="links-vertical mt-2">
            {footerContactLinks?.map((item, index) => (
              <li key={`footer-list-${index}`}>
                {item?.name} {item?.value}
              </li>
            ))}
          </ul>
        )}
      </Col>
    );
  };

  const _renderHorizontal = () => {
    return <hr className="bg-gray-600 my-5" />;
  };

  const _renderFooterCopyRight = () => {
    return (
      <Row>
        <Col className="mb-md-2">
          <Card.Link
            href={footerCopyright?.brandLink}
            target={footerCopyright?.target ?? '_self'}
            className="d-flex justify-content-center"
          >
            <Image
              src={footerCopyright?.brandLogo}
              height={footerCopyright?.brandLogoHeight ?? 25}
              className="d-block mx-auto mb-3"
            />
          </Card.Link>
          <div
            className="d-flex text-center justify-content-center align-items-center"
            role="contentinfo"
          >
            <p className="font-weight-normal font-small mb-0">
              {`Copyright © ${footerCopyright?.copyrightTitle} ${footerCopyright?.startYear}-`}
              <span className="current-year">{currentYear}</span>. All rights
              reserved.
            </p>
          </div>
        </Col>
      </Row>
    );
  };

  return (
    <footer className="footer py-6 bg-primary text-white">
      <Container>
        <Row>
          {_renderBrand()}
          {_renderUsefulLinks()}
          {_renderServiceLinks()}
          {_renderAddress()}
        </Row>
        {_renderHorizontal()}
        {_renderFooterCopyRight()}
      </Container>
    </footer>
  );
};

Footer.defaultProps = {
  footerBrandLink: '#root',
};
